import type { ArchivedChunk, BlockView, MergeEvent } from './types';
import { BLOCK_SIZE, PLAYFIELD_INSET, WORLD_WIDTH, clamp, formatTier } from './utils';

const DAY_COLORS = ['#c89b3c', '#c75b4b', '#4e7693', '#68806a'];
const NIGHT_COLORS = ['#d2a74f', '#d06a5c', '#668ba5', '#7f987f'];

interface Particle extends MergeEvent { born: number }

export class GameRenderer {
  readonly context: CanvasRenderingContext2D;
  width = 0;
  height = 0;
  worldHeight = 0;
  scale = 1;
  cameraBottom = 28;
  targetBottom = 28;
  previewX = WORLD_WIDTH / 2;
  previewTier = 2;
  showPreview = true;
  reducedMotion = false;
  nightMode = false;
  private particles: Particle[] = [];
  private dpr = 1;

  constructor(readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D is unavailable');
    this.context = context;
    this.resize();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = Math.max(1, rect.width);
    this.height = Math.max(1, rect.height);
    this.scale = this.width / WORLD_WIDTH;
    this.worldHeight = this.height / this.scale;
    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
  }

  updateCamera(highestY: number, deltaSeconds: number): void {
    const candidate = Math.min(28, highestY + this.worldHeight * 0.69);
    this.targetBottom = candidate;
    const smoothing = this.reducedMotion ? 1 : 1 - Math.exp(-deltaSeconds * 4.8);
    this.cameraBottom += (this.targetBottom - this.cameraBottom) * smoothing;
  }

  spawnY(): number {
    return this.cameraBottom - this.worldHeight + 86;
  }

  clientToWorldX(clientX: number): number {
    const rect = this.canvas.getBoundingClientRect();
    return clamp(
      (clientX - rect.left) / this.scale,
      PLAYFIELD_INSET + BLOCK_SIZE / 2,
      WORLD_WIDTH - PLAYFIELD_INSET - BLOCK_SIZE / 2,
    );
  }

  addMergeParticle(event: MergeEvent): void {
    this.particles.push({ ...event, born: performance.now() });
  }

  render(blocks: BlockView[], archived: ArchivedChunk[]): void {
    const ctx = this.context;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.width, this.height);
    this.drawWorldGrid();
    this.drawPlayfieldInsets();
    ctx.save();
    ctx.scale(this.scale, this.scale);
    ctx.translate(0, this.worldHeight - this.cameraBottom);
    for (const chunk of archived) {
      for (const record of chunk.blocks) this.drawBlock(record.x, record.y, record.angle, record.tier, 0.72);
    }
    for (const body of blocks) {
      this.drawBlock(body.position.x, body.position.y, body.angle, body.tier, 1);
    }
    this.drawParticles();
    if (this.showPreview) this.drawPreview();
    ctx.restore();
  }

  private drawWorldGrid(): void {
    const ctx = this.context;
    const grid = 24 * this.scale;
    const offsetY = ((this.worldHeight - this.cameraBottom) * this.scale) % grid;
    ctx.save();
    ctx.strokeStyle = this.nightMode ? 'rgba(238,233,220,.14)' : 'rgba(16,16,16,.14)';
    ctx.lineWidth = 1;
    for (let x = 0; x <= this.width; x += grid) {
      ctx.beginPath(); ctx.moveTo(Math.round(x) + 0.5, 0); ctx.lineTo(Math.round(x) + 0.5, this.height); ctx.stroke();
    }
    for (let y = offsetY; y <= this.height; y += grid) {
      ctx.beginPath(); ctx.moveTo(0, Math.round(y) + 0.5); ctx.lineTo(this.width, Math.round(y) + 0.5); ctx.stroke();
    }
    ctx.restore();
  }

  private drawPreview(): void {
    const ctx = this.context;
    const y = this.spawnY();
    ctx.save();
    ctx.strokeStyle = this.nightMode ? 'rgba(238,233,220,.52)' : 'rgba(16,16,16,.46)';
    ctx.setLineDash([4, 6]);
    ctx.lineWidth = 1 / this.scale;
    const guideOffset = BLOCK_SIZE / 2;
    for (const guideX of [this.previewX - guideOffset, this.previewX + guideOffset]) {
      ctx.beginPath();
      ctx.moveTo(guideX, y + BLOCK_SIZE / 2);
      ctx.lineTo(guideX, this.cameraBottom - 18);
      ctx.stroke();
    }
    ctx.restore();
    this.drawBlock(this.previewX, y, 0, this.previewTier, 0.78);
  }

  private drawPlayfieldInsets(): void {
    const ctx = this.context;
    const inset = PLAYFIELD_INSET * this.scale;
    ctx.save();
    ctx.fillStyle = this.nightMode ? 'rgba(21,23,19,.78)' : 'rgba(246,242,233,.74)';
    ctx.fillRect(0, 0, inset, this.height);
    ctx.fillRect(this.width - inset, 0, inset, this.height);
    ctx.strokeStyle = this.nightMode ? 'rgba(238,233,220,.58)' : 'rgba(16,16,16,.55)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(Math.round(inset) + 0.5, 0);
    ctx.lineTo(Math.round(inset) + 0.5, this.height);
    ctx.moveTo(Math.round(this.width - inset) + 0.5, 0);
    ctx.lineTo(Math.round(this.width - inset) + 0.5, this.height);
    ctx.stroke();
    ctx.restore();
  }

  private drawBlock(x: number, y: number, angle: number, tier: number, alpha: number): void {
    const ctx = this.context;
    const colors = this.nightMode ? NIGHT_COLORS : DAY_COLORS;
    const color = colors[(tier - 1) % colors.length];
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = color;
    ctx.strokeStyle = '#101010';
    ctx.lineWidth = 1.2 / this.scale;
    ctx.fillRect(-BLOCK_SIZE / 2, -BLOCK_SIZE / 2, BLOCK_SIZE, BLOCK_SIZE);
    ctx.strokeRect(-BLOCK_SIZE / 2, -BLOCK_SIZE / 2, BLOCK_SIZE, BLOCK_SIZE);
    ctx.save();
    ctx.beginPath();
    ctx.rect(-BLOCK_SIZE / 2, -BLOCK_SIZE / 2, BLOCK_SIZE, BLOCK_SIZE);
    ctx.clip();
    ctx.strokeStyle = 'rgba(16,16,16,.16)';
    ctx.lineWidth = 0.75 / this.scale;
    const spacing = 8;
    for (let line = -BLOCK_SIZE; line < BLOCK_SIZE * 2; line += spacing) {
      ctx.beginPath(); ctx.moveTo(line, BLOCK_SIZE / 2); ctx.lineTo(line + BLOCK_SIZE, -BLOCK_SIZE / 2); ctx.stroke();
    }
    ctx.restore();
    ctx.fillStyle = '#101010';
    ctx.font = `500 ${tier > 19 ? 9 : 11}px "IBM Plex Mono"`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(formatTier(tier), 0, 0);
    ctx.restore();
  }

  private drawParticles(): void {
    const now = performance.now();
    this.particles = this.particles.filter((particle) => now - particle.born < 700);
    const ctx = this.context;
    for (const particle of this.particles) {
      const progress = (now - particle.born) / 700;
      const radius = 12 + progress * 42;
      ctx.save();
      ctx.globalAlpha = 1 - progress;
      ctx.strokeStyle = this.nightMode ? '#eee9dc' : '#101010';
      ctx.lineWidth = 1.5 / this.scale;
      ctx.strokeRect(particle.x - radius / 2, particle.y - radius / 2, radius, radius);
      ctx.restore();
    }
  }

}
